import { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Popper,
  Portal,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { PopperPlacementType } from '@mui/material/Popper';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import {
  COACHMARK_SUBTITLE_SX,
  COACHMARK_TITLE_SX,
} from '../constants/coachmark-typography';

const HINT_CARD_WIDTH = 320;

/** Viewport width where side (left/right) tips become unreliable. */
export const COACHMARK_MOBILE_BREAKPOINT = 'md' as const;

type HintSide = 'top' | 'bottom' | 'left' | 'right';

/** Place the tip relative to the anchor; shift start/end near dialog edges for top/bottom. */
export function getTabHintPlacement(
  anchor: HTMLElement,
  side: HintSide = 'top',
): PopperPlacementType {
  // Keep left/right as requested — Popper preventOverflow will nudge into the viewport
  // so Create/Add tips stay on the right side of the screen instead of flipping left.
  if (side === 'left' || side === 'right') {
    return side;
  }

  const rect = anchor.getBoundingClientRect();
  const dialog = anchor.closest('.MuiDialog-paper') as HTMLElement | null;
  const bounds = dialog?.getBoundingClientRect() ?? {
    left: 0,
    width: window.innerWidth,
  };

  const midX = rect.left + rect.width / 2;
  const relativeX = (midX - bounds.left) / Math.max(bounds.width, 1);
  const align =
    relativeX <= 0.35 ? 'start' : relativeX >= 0.65 ? 'end' : '';

  if (!align) return side;
  return `${side}-${align}` as PopperPlacementType;
}

type UseFormTabHintArgs = {
  /** When the dialog opens, show hints only if this is true (typically add mode). */
  enabled: boolean;
  dialogOpen: boolean;
  activeTab: number;
  /** Prefer tip above/below (tabs/fields) or beside (left/right of dialog). */
  side?: HintSide;
};

export function useFormTabHint({
  enabled,
  dialogOpen,
  activeTab,
  side = 'top',
}: UseFormTabHintArgs) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(COACHMARK_MOBILE_BREAKPOINT));
  // Side tips break on small screens — prefer below the section.
  const resolvedSide: HintSide =
    isMobile && (side === 'left' || side === 'right') ? 'bottom' : side;

  const [showHint, setShowHint] = useState(enabled);
  const [hintAnchor, setHintAnchor] = useState<HTMLElement | null>(null);
  const [hintPlacement, setHintPlacement] = useState<PopperPlacementType>(resolvedSide);
  const tabRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (dialogOpen) {
      setShowHint(enabled);
    }
  }, [dialogOpen, enabled]);

  useEffect(() => {
    if (!showHint) {
      setHintAnchor(null);
      return;
    }

    const updateAnchor = () => {
      const el = tabRefs.current[activeTab] ?? null;
      setHintAnchor(el);
      if (el) {
        setHintPlacement(getTabHintPlacement(el, resolvedSide));
      }
    };

    const frame = window.requestAnimationFrame(updateAnchor);
    window.addEventListener('resize', updateAnchor);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateAnchor);
    };
  }, [showHint, activeTab, dialogOpen, resolvedSide]);

  const getTabSx = (index: number): SxProps<Theme> | undefined =>
    showHint && index === activeTab
      ? {
          position: 'relative',
          zIndex: 1,
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 3,
          borderRadius: 1.5,
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
        }
      : undefined;

  const setTabRef = (index: number) => (el: HTMLElement | null) => {
    tabRefs.current[index] = el;
  };

  return {
    showHint,
    dismissHint: () => setShowHint(false),
    hintAnchor,
    hintPlacement,
    setTabRef,
    getTabSx,
  };
}

type FormTabHintCoachmarkProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  placement: PopperPlacementType;
  title: string;
  subtitle: string;
  onDismiss: () => void;
  /** When true, flip to a visible side if the preferred placement overflows (submit tips). */
  allowFlip?: boolean;
  /**
   * Keep the tip on the requested side of the anchor (e.g. right of Add).
   * Disables flip and main-axis overflow shifting that would move it to the other side.
   */
  stickToPlacement?: boolean;
};

function CoachmarkCardContent({
  title,
  subtitle,
  onDismiss,
  showArrow,
  arrowSx,
}: {
  title: string;
  subtitle: string;
  onDismiss: () => void;
  showArrow?: boolean;
  arrowSx?: object;
}) {
  const { t } = useTranslation();

  return (
    <>
      {showArrow ? (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: 16,
            height: 16,
            bgcolor: 'background.paper',
            borderColor: 'divider',
            transform: 'rotate(45deg)',
            ...arrowSx,
          }}
        />
      ) : null}
      <IconButton
        aria-label={t('common.close')}
        size="small"
        onClick={onDismiss}
        sx={{
          position: 'absolute',
          top: 8,
          insetInlineEnd: 8,
          color: 'text.disabled',
          p: 0.5,
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Typography
        sx={{
          ...COACHMARK_TITLE_SX,
          color: 'text.primary',
          mb: 0.5,
          px: 2.5,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ ...COACHMARK_SUBTITLE_SX, color: 'text.secondary' }}>
        {subtitle}
      </Typography>
    </>
  );
}

const coachmarkCardSx = {
  position: 'relative' as const,
  textAlign: 'center' as const,
  px: 2.5,
  py: 1.75,
  bgcolor: 'background.paper',
  borderRadius: 2.5,
  border: 1,
  borderColor: 'divider',
  boxShadow: '0 10px 32px rgba(15, 23, 42, 0.16)',
};

/** Remap desktop left/right tips for narrow viewports. */
function resolveMobilePlacement(placement: PopperPlacementType): PopperPlacementType {
  if (placement === 'right' || placement.startsWith('right-')) {
    // Keep Create/Add tips above the button so the CTA stays tappable.
    return 'top';
  }
  if (placement === 'left' || placement.startsWith('left-')) {
    return 'bottom';
  }
  return placement;
}

/** Non-blocking bottom sheet — form stays interactive underneath. */
function MobileBottomSheetCoachmark({
  open,
  title,
  subtitle,
  onDismiss,
}: {
  open: boolean;
  title: string;
  subtitle: string;
  onDismiss: () => void;
}) {
  const theme = useTheme();
  if (!open) return null;

  return (
    <Portal>
      <Box
        role="dialog"
        aria-label={title}
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: theme.zIndex.modal + 10,
          px: 2,
          pt: 1,
          pb: 'max(12px, env(safe-area-inset-bottom))',
          // Allow tapping fields behind the sheet chrome.
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            ...coachmarkCardSx,
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: 480,
            mx: 'auto',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            filter: `drop-shadow(0 -4px 24px ${alpha(theme.palette.common.black, 0.18)})`,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              bgcolor: 'divider',
              mx: 'auto',
              mb: 1.25,
            }}
          />
          <CoachmarkCardContent title={title} subtitle={subtitle} onDismiss={onDismiss} />
        </Box>
      </Box>
    </Portal>
  );
}

/** White coachmark card anchored to a form tab or submit button. */
export function FormTabHintCoachmark({
  open,
  anchorEl,
  placement,
  title,
  subtitle,
  onDismiss,
  allowFlip = false,
  stickToPlacement = false,
}: FormTabHintCoachmarkProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(COACHMARK_MOBILE_BREAKPOINT));

  // Section tips (left) → bottom sheet on mobile. Submit tips (right) → above the button.
  const useBottomSheet =
    isMobile &&
    open &&
    (placement === 'left' || placement.startsWith('left-'));

  if (useBottomSheet) {
    return (
      <MobileBottomSheetCoachmark
        open={open}
        title={title}
        subtitle={subtitle}
        onDismiss={onDismiss}
      />
    );
  }

  const effectivePlacement = isMobile ? resolveMobilePlacement(placement) : placement;
  const effectiveStick = isMobile ? false : stickToPlacement;
  const effectiveAllowFlip = isMobile ? true : allowFlip;

  const isBelow = effectivePlacement.startsWith('bottom');
  const isLeft = effectivePlacement === 'left' || effectivePlacement.startsWith('left-');
  const isRight = effectivePlacement === 'right' || effectivePlacement.startsWith('right-');
  const arrowAlign = effectivePlacement.endsWith('-start')
    ? '22%'
    : effectivePlacement.endsWith('-end')
      ? '78%'
      : '50%';

  // Popper left/right are physical. stylis-plugin-rtl flips left↔right (and
  // box-shadow X), so under RTL we write the opposite side to keep the arrow
  // on the edge that faces the anchored field.
  const rtl = theme.direction === 'rtl';

  const arrowSx = isLeft
    ? rtl
      ? {
          left: -9,
          top: '50%',
          mt: '-8px',
          borderLeft: 1,
          borderTop: 1,
          boxShadow: '-1px -1px 3px rgba(15, 23, 42, 0.06)',
        }
      : {
          right: -9,
          top: '50%',
          mt: '-8px',
          borderRight: 1,
          borderTop: 1,
          boxShadow: '1px -1px 3px rgba(15, 23, 42, 0.06)',
        }
    : isRight
      ? rtl
        ? {
            right: -9,
            top: '50%',
            mt: '-8px',
            borderRight: 1,
            borderBottom: 1,
            boxShadow: '1px 1px 3px rgba(15, 23, 42, 0.06)',
          }
        : {
            left: -9,
            top: '50%',
            mt: '-8px',
            borderLeft: 1,
            borderBottom: 1,
            boxShadow: '-1px 1px 3px rgba(15, 23, 42, 0.06)',
          }
      : isBelow
        ? rtl
          ? {
              right: arrowAlign,
              top: -9,
              mr: '-8px',
              borderRight: 1,
              borderTop: 1,
              boxShadow: '1px -1px 3px rgba(15, 23, 42, 0.06)',
            }
          : {
              left: arrowAlign,
              top: -9,
              ml: '-8px',
              borderLeft: 1,
              borderTop: 1,
              boxShadow: '-1px -1px 3px rgba(15, 23, 42, 0.06)',
            }
        : rtl
          ? {
              right: arrowAlign,
              bottom: -9,
              mr: '-8px',
              borderLeft: 1,
              borderBottom: 1,
              boxShadow: '-2px 2px 4px rgba(15, 23, 42, 0.06)',
            }
          : {
              left: arrowAlign,
              bottom: -9,
              ml: '-8px',
              borderRight: 1,
              borderBottom: 1,
              boxShadow: '2px 2px 4px rgba(15, 23, 42, 0.06)',
            };

  const canFlip = effectiveAllowFlip && !effectiveStick;

  return (
    <Popper
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement={effectivePlacement}
      modifiers={[
        { name: 'offset', options: { offset: [0, 16] } },
        {
          name: 'preventOverflow',
          options: {
            padding: 12,
            altAxis: true,
            // Keep tip on the preferred side of the button (e.g. right of Add).
            mainAxis: !effectiveStick,
            boundary: 'viewport',
          },
        },
        canFlip
          ? {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: isMobile
                  ? ['bottom', 'top', 'left', 'right']
                  : ['left', 'top-end', 'top'],
              },
            }
          : { name: 'flip', enabled: false },
      ]}
      sx={{ zIndex: (zTheme) => zTheme.zIndex.modal + 10 }}
    >
      <Box
        sx={{
          ...coachmarkCardSx,
          width: HINT_CARD_WIDTH,
          maxWidth: 'calc(100vw - 48px)',
          filter: `drop-shadow(0 2px 8px ${alpha(theme.palette.common.black, 0.08)})`,
        }}
      >
        <CoachmarkCardContent
          title={title}
          subtitle={subtitle}
          onDismiss={onDismiss}
          showArrow
          arrowSx={arrowSx}
        />
      </Box>
    </Popper>
  );
}
